// WooCommerce checkout page (woocommerce/checkout/*.php overrides): customer-type toggle, payment radio cards,
// coupon form via our REST cart (then WC's update_checkout refreshes the review fragments) and the hold timer.
// WC's own checkout JS (jQuery) keeps doing the AJAX order review / order submission.
import { qs, qsa, on, t } from './dom.js';
import { applyCoupon, removeCoupon, loadCart, subscribe, holdCountdown, holdSecondsLeft } from './cart.js';
import { track, config } from './track.js';

const $ = () => window.jQuery;

function syncCustomerType(form) {
  const checked = qs('input[name="billing_customer_type"]:checked', form);
  const company = !!checked && checked.value === 'company';
  qsa('.kt-company-field', form).forEach((row) => { row.hidden = !company; });
  qsa('.kt-customer-type label.radio', form).forEach((label) => {
    const input = qs('#' + label.getAttribute('for'), form);
    label.classList.toggle('is-selected', !!(input && input.checked));
  });
}

function syncPaymentCards(root = document) {
  qsa('.wc_payment_method', root).forEach((li) => {
    const input = qs('input[name="payment_method"]', li);
    li.classList.toggle('is-selected', !!(input && input.checked));
  });
}

function triggerUpdate() {
  if ($()) $()(document.body).trigger('update_checkout');
  else window.location.reload();
}

/**
 * 1.9.11 (KULTUR-199): "Ihre Zahlung wird gerade durchgeführt – bitte warten" overlay (template-parts/checkout/paying-overlay.php)
 * from the click on "Kaufen" until the redirect – or until an error brings the form back. Shown on WC's checkout_place_order
 * (checkout) or the submit of form#order_review (pay-for-order page). Hidden on checkout_error, on an error notice added
 * to the form, on a back-forward cache restore, and by a watchdog when the form is idle (not .processing, no navigation
 * started) for IDLE_MS – e.g. a gateway script that stopped with its own inline error or a provider's 3DS modal (which sits
 * above the overlay anyway). Focus moves into the dialog and back to the trigger; text is announced via an aria-live region.
 */
const IDLE_MS = 10000;
export function initPayingOverlay(form) {
  const ov = qs('[data-paying-overlay]');
  if (!ov || !form) return null;
  const box = qs('[data-paying-box]', ov);
  const live = qs('[data-paying-live]');
  let shown = false;
  let leaving = false;
  let idleSince = 0;
  let back = null;
  let watch = 0;
  const hide = () => {
    if (!shown) return;
    shown = false;
    ov.hidden = true;
    form.removeAttribute('aria-busy');
    if (live) live.textContent = '';
    clearInterval(watch);
    // WC focuses its error notice – only give the focus back when it is still on the overlay / nowhere
    const a = document.activeElement;
    if (back && document.contains(back) && (!a || a === document.body || ov.contains(a))) back.focus({ preventScroll: true });
  };
  const show = () => {
    leaving = false;
    idleSince = 0;
    if (shown) return;
    shown = true;
    back = document.activeElement;
    ov.hidden = false;
    form.setAttribute('aria-busy', 'true');
    if (live) live.textContent = (qs('[data-paying-title]', ov) || ov).textContent.trim();
    if (box) box.focus({ preventScroll: true });
    clearInterval(watch);
    watch = setInterval(() => {
      if (!shown || leaving) return;
      if (form.classList.contains('processing')) { idleSince = 0; return; }
      idleSince = idleSince || Date.now();
      if (Date.now() - idleSince >= IDLE_MS) hide();
    }, 500);
  };
  ['beforeunload', 'pagehide'].forEach((ev) => window.addEventListener(ev, () => { if (shown) leaving = true; }));
  window.addEventListener('pageshow', (e) => { if (e.persisted) { leaving = false; hide(); } });
  ov.addEventListener('keydown', (e) => { if (e.key === 'Tab') { e.preventDefault(); if (box) box.focus(); } }); // keep focus inside while paying
  // error notices (WC submit_error, gateway scripts) → back to the form
  new MutationObserver((list) => {
    if (!shown) return;
    for (const m of list) {
      for (const n of m.addedNodes) {
        if (n.nodeType === 1 && (n.matches('.woocommerce-error, .woocommerce-NoticeGroup-checkout, .woocommerce-notices-wrapper ul[role="alert"]') || n.querySelector?.('.woocommerce-error'))) { hide(); return; }
      }
    }
  }).observe(form.parentNode || form, { childList: true, subtree: true });
  if ($()) $()(document.body).on('checkout_error', hide);
  return { show, hide, isShown: () => shown };
}

export function initCheckout() {
  // Stripe test mode: click a sample card number to copy it (WooCommerce re-renders the payment box, so delegate).
  on(document, 'click', '[data-copy-text]', async (e, btn) => {
    e.preventDefault();
    try { await navigator.clipboard.writeText(btn.dataset.copyText); btn.classList.add('ring-2', 'ring-amber-400'); setTimeout(() => btn.classList.remove('ring-2', 'ring-amber-400'), 900); } catch { /* clipboard blocked – number stays visible */ }
  });
  // Pay-for-order page (/kasse/order-pay/…): plain form post (gateways may intercept it) → overlay on submit
  const payForm = qs('form#order_review');
  if (payForm && !payForm.classList.contains('woocommerce-checkout')) {
    const pay = initPayingOverlay(payForm);
    if (pay) payForm.addEventListener('submit', () => pay.show());
  }
  const form = qs('form.woocommerce-checkout');
  if (!form) return;
  const paying = initPayingOverlay(form);

  syncCustomerType(form);
  on(form, 'change', 'input[name="billing_customer_type"]', () => syncCustomerType(form));

  syncPaymentCards(form);
  on(document, 'change', 'input[name="payment_method"]', () => syncPaymentCards(form));
  if ($()) $()(document.body).on('updated_checkout', () => { syncPaymentCards(form); });

  // Coupon (inside the review fragment, so delegate on the document).
  on(document, 'click', '[data-checkout-coupon-apply]', async (e, btn) => {
    e.preventDefault();
    const input = qs('[data-checkout-coupon]', btn.closest('[data-checkout-coupon-box]') || form);
    if (!input || !input.value.trim()) { if (input) input.focus(); return; }
    btn.disabled = true;
    const ok = await applyCoupon(input.value.trim());
    btn.disabled = false;
    if (ok) { input.value = ''; triggerUpdate(); }
  });
  on(document, 'keydown', '[data-checkout-coupon]', (e, input) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const btn = qs('[data-checkout-coupon-apply]', input.closest('[data-checkout-coupon-box]') || form);
    if (btn) btn.click();
  });
  on(document, 'click', '[data-checkout-coupon-remove]', async (e, btn) => {
    e.preventDefault();
    btn.disabled = true;
    const ok = await removeCoupon(btn.dataset.code || '');
    if (ok) triggerUpdate(); else btn.disabled = false;
  });

  // Hold timer in the summary card.
  const timer = qs('[data-checkout-timer]');
  if (timer) {
    let state = null;
    const render = () => {
      const txt = holdCountdown(state);
      timer.hidden = !txt;
      if (txt) qs('[data-checkout-timer-text]', timer).textContent = t('woo.reserved_for', { time: txt });
      if (txt && holdSecondsLeft(state) === 0) window.location.href = window.KT.cartUrl;
    };
    subscribe((s) => { state = s; render(); });
    setInterval(render, 1000);
    loadCart();
  }

  // Submit feedback: WC blocks the form with blockUI; add our own busy state on the button.
  const btn = qs('#place_order');
  // 1.9.8 tracking: add_payment_info once per page when the order is submitted (chosen payment method; payload = begin_checkout)
  let paid = false;
  const paymentInfo = () => {
    const co = (config() || {}).co;
    if (paid || !co) return;
    paid = true;
    const pm = qs('input[name="payment_method"]:checked', form);
    track('add_payment_info', { ...co, payment_type: pm ? pm.value : '' });
  };
  if (btn && $()) {
    $()(form).on('checkout_place_order', () => { btn.classList.add('is-busy'); paymentInfo(); if (paying) paying.show(); });
    $()(document.body).on('checkout_error', () => { btn.classList.remove('is-busy'); });
  }
}
